/**
 * Step 6: the finished article, as it will look, and the file to publish it with.
 *
 * The preview is rendered by the server, not by this screen. That is deliberate:
 * a preview drawn by different code from the export is a preview that eventually
 * lies, and the one thing this screen is for is being able to trust what you are
 * about to publish. So it fetches the HTML export and shows exactly those bytes.
 *
 * PDF is the browser's own print dialogue. It produces a better file than
 * anything this application would assemble, it needs no server code, and every
 * machine already has it.
 */
import { useEffect, useState } from 'react';
import { Link, useLoaderData } from 'react-router';
import {
  ApiError,
  get,
  type ArticleDetail,
  type ComplianceReport,
  type GenerationContext,
} from '../api.ts';
import { Card, Empty, Pill, jaNumber } from '../components/ui.tsx';
import { visibleLength } from '../../../shared/rich-format.ts';

interface LoaderData {
  article: ArticleDetail;
  context: GenerationContext;
}

/**
 * The four formats, and what each one actually hands over.
 *
 * The second line is what a writer needs to choose and never had: whether the
 * file carries its pictures. The standalone page does - they are embedded, so it
 * opens on a desktop with no server behind it - and the WordPress fragment does
 * not, because base64 pasted into a post is a megabyte of markup and no entry in
 * the media library. Saying so here is the difference between choosing a format
 * and finding out afterwards.
 */
const FORMATS = [
  {
    value: 'html',
    label: 'HTML（1ファイル）',
    hint: 'そのまま開ける完成ページ。',
    images: '画像を埋め込むので、この1ファイルだけで表示できます',
  },
  {
    value: 'wp',
    label: 'WordPress用',
    hint: '本文の断片＋カテゴリ・公開日のメモ。',
    images: '画像は別途アップロードし、URLを差し替えてください',
  },
  { value: 'md', label: 'Markdown', hint: '原稿として保管する場合。', images: '画像はリンクのみ' },
  { value: 'txt', label: 'テキスト', hint: '装飾なしのCMS入力欄向け。', images: '画像は含みません' },
] as const;

/** The enum is for the database; this screen shows what it means. */
const STATUS_LABEL: Record<string, string> = {
  DRAFT: '下書き',
  IN_REVIEW: '要確認',
  APPROVED: '承認済み',
  PUBLISHED: '公開済み',
  ARCHIVED: 'アーカイブ',
};

export default function ArticleFinal() {
  const { article, context } = useLoaderData<LoaderData>();
  const [preview, setPreview] = useState<string | null>(null);
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  /*
   * How tall the exported page actually is.
   *
   * The preview was a 70vh box with its own scrollbar - a window onto the
   * article rather than the article. Nobody reads a finished page through a
   * letterbox, and the one question this screen answers is "is this right to
   * publish", which needs the whole thing in one flow with the page's own
   * scroll. So the frame is grown to its content and never scrolls itself.
   */
  const [previewHeight, setPreviewHeight] = useState(600);

  /*
   * The preview is the export. Fetched as text and put in a sandboxed iframe:
   * the markup is clinic-authored, the renderer escapes it, and the sandbox is
   * the belt to that braces - nothing in there runs, navigates or reaches the
   * session.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/v1/articles/${article.id}/export?format=html`, {
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        if (!cancelled) setPreview(text);
      } catch {
        if (!cancelled) setError('プレビューを取得できませんでした');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [article.id, article.version]);

  /* The compliance check again, here, because this is the last look before it
     goes out - and it costs nothing to run. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await get<ComplianceReport>(`/articles/${article.id}/compliance`);
        if (!cancelled) setReport(result);
      } catch {
        // Not fatal: the editor has the same check with its own error handling.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [article.id, article.version]);

  /**
   * Download one format.
   *
   * Through fetch rather than a plain link: the request needs the session cookie
   * and the response carries the filename, and an <a download> would ignore the
   * server's Content-Disposition for a cross-format name.
   */
  async function download(format: string) {
    setDownloading(format);
    setError(null);
    try {
      const res = await fetch(`/api/v1/articles/${article.id}/export?format=${format}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw new ApiError(res.status, 'export_failed', '書き出せませんでした');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // The server sends the real name; this is the fallback the browser uses
      // when it cannot read the header.
      link.download = `${article.title || 'article'}.${format === 'wp' ? 'wp.html' : format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '書き出せませんでした');
    } finally {
      setDownloading(null);
    }
  }

  /**
   * Grow the frame to whatever the article turned out to be.
   *
   * Readable from here because the sandbox now grants `allow-same-origin` and
   * nothing else. That pair is the safe one: the document is same-origin, so its
   * height can be measured, and `allow-scripts` is still withheld - so nothing
   * in it runs, and the combination that would defeat the sandbox entirely
   * (scripts AND same-origin) never exists. Forms, popups, top-level navigation
   * and downloads all remain denied by default.
   *
   * Re-measured on resize, because the column reflows and a Japanese paragraph
   * that was three lines wide becomes five on a narrow window.
   */
  function fitPreview() {
    const frame = document.getElementById('final-preview') as HTMLIFrameElement | null;
    const doc = frame?.contentDocument;
    if (!doc?.body) return;
    // scrollHeight of the documentElement, not the body: the body has a bottom
    // margin the export's own stylesheet sets, and measuring the body loses it.
    setPreviewHeight(Math.max(400, doc.documentElement.scrollHeight));
  }

  useEffect(() => {
    if (preview === null) return;
    window.addEventListener('resize', fitPreview);
    return () => window.removeEventListener('resize', fitPreview);
  }, [preview]);

  /**
   * Make the article's own 目次 work.
   *
   * The exported page has been correct all along: every heading carries an
   * `id="sec-N"` and the contents block links to it, which is what makes the
   * downloaded file navigable. What broke it here is this screen - the frame is
   * now grown to the full article and told not to scroll, so a fragment jump
   * inside it has nowhere to go. The scrolling belongs to the page.
   *
   * So the click is caught and translated: the heading's offset inside the
   * document, plus the frame's offset in the page. Delegated on the document
   * rather than bound per link, because the export is re-fetched whenever the
   * article changes and a listener per anchor would have to be unbound.
   */
  function wireContents() {
    const frame = document.getElementById('final-preview') as HTMLIFrameElement | null;
    const doc = frame?.contentDocument;
    if (!doc || !frame) return;
    doc.addEventListener('click', (event) => {
      const link = (event.target as Element | null)?.closest?.('a[href^="#"]');
      const id = link?.getAttribute('href')?.slice(1);
      if (!id) return;
      const target = doc.getElementById(id);
      if (!target) return;
      event.preventDefault();
      /*
       * A little above the heading, not flush with the window's edge: a heading
       * pinned to the top of the viewport reads as the end of what came before.
       */
      const top =
        target.getBoundingClientRect().top + frame.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  /**
   * Print, from the preview's own document.
   *
   * The iframe holds the exported page with its own print styles, so printing it
   * gives the article - not this screen with its cards and sidebar around it.
   */
  function print() {
    const frame = document.getElementById('final-preview') as HTMLIFrameElement | null;
    frame?.contentWindow?.focus();
    frame?.contentWindow?.print();
  }

  const missing = context.missing;

  /*
   * The export panel, above the preview and below it.
   *
   * The same card twice rather than one at the bottom, because the preview is
   * now a full-length page: an article of five thousand characters puts the only
   * download button several screens below the fold, and somebody who has decided
   * at the top should not have to scroll the whole article to act on it. Two
   * identical panels are cheaper than a floating bar that covers the thing being
   * reviewed.
   */
  const exportCard = (
        <Card title="書き出し" caption="用途に合わせて選んでください。">
          {article.body_md.trim() === '' ? (
            <Empty>本文がまだありません。編集画面で本文を作成してください。</Empty>
          ) : (
            /*
              One card per format, with the button at the foot.

              They used to be `.tile-drag` - the component the 差し込み panel uses
              for things you DRAG into the article - with the button squeezed to
              the right of two lines of text. Nothing here is draggable and the
              widths were set by the length of the Japanese in each hint, so four
              choices that are equals looked like four different kinds of thing.
            */
            <div className="exports">
              {FORMATS.map((format) => (
                <div className="exports__card" key={format.value}>
                  <div className="exports__name">{format.label}</div>
                  <div className="muted small">{format.hint}</div>
                  <div className="exports__images">{format.images}</div>
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={downloading !== null}
                    onClick={() => void download(format.value)}
                  >
                    {downloading === format.value ? '準備中…' : '保存'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="muted small" style={{ marginBottom: 0 }}>
            PDFは「印刷 / PDF」から、ブラウザの印刷ダイアログで保存してください。書き出したファイルには
            運営法人・所在地・広告管理責任者の記載が入ります（登録済みの項目のみ）。
          </p>
        </Card>
  );


  return (
    <>
      <Card
        title="最終稿"
        caption={`${article.store_name}・第${article.version}版`}
        actions={
          <>
            <button className="btn btn--sm" type="button" onClick={print} disabled={preview === null}>
              印刷 / PDF
            </button>
            <Link className="btn btn--ghost btn--sm" to={`/articles/${article.id}`}>
              編集に戻る
            </Link>
          </>
        }
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="grid grid--4">
          <div className="stat">
            <div className="stat__label">状態</div>
            <div className="stat__value stat__value--sm">
              {STATUS_LABEL[article.status] ?? article.status}
            </div>
            <div className="muted small">公開想定：{article.publish_on ?? '未定'}</div>
          </div>
          <div className="stat">
            <div className="stat__label">文字数（本文）</div>
            <div className="stat__value stat__value--sm">
              {jaNumber(visibleLength(article.body_md))}
            </div>
            <div className="muted small">目安 4,000〜5,000</div>
          </div>
          <div className="stat">
            <div className="stat__label">WordPressカテゴリ</div>
            <div className="stat__value stat__value--sm">
              {context.resolved.wordpressCategory ?? '未設定'}
            </div>
            <div className="muted small">店舗設定 → クリニック設定の順に適用</div>
          </div>
          <div className="stat">
            <div className="stat__label">薬機法チェック</div>
            <div className="stat__value stat__value--sm">
              {report ? (
                <Pill tone={report.verdict === '指摘なし' ? 'ok' : 'warn'}>{report.verdict}</Pill>
              ) : (
                '—'
              )}
            </div>
            <div className="muted small">
              {report ? `指摘 ${report.findings.length} 件` : '確認中'}
            </div>
          </div>
        </div>

        {missing.length > 0 && (
          <p className="muted small">
            未登録のため記事に記載できなかった情報：{missing.join('、')}
          </p>
        )}
        {report && report.findings.length > 0 && (
          <p className="muted small">
            表現の指摘が残っています。編集画面の「薬機法・医療広告チェック」で確認してください。
          </p>
        )}
      </Card>

      {exportCard}

      <Card
        title="プレビュー"
        caption="書き出されるHTMLそのものを、公開されるままの長さで表示しています。"
      >
        {preview === null ? (
          <p className="muted small" style={{ marginTop: 0 }}>
            読み込み中…
          </p>
        ) : (
          /*
           * srcDoc + sandbox with nothing granted: no scripts, no forms, no
           * navigation, no same-origin access. The renderer escapes the body
           * already; this is the second lock on the same door.
           */
          /*
           * srcDoc + a sandbox that grants same-origin and nothing else: no
           * scripts, no forms, no navigation, no downloads. The renderer escapes
           * the body already; this is the second lock on the same door, and
           * same-origin alone opens none of them - it only lets this screen ask
           * the document how tall it is.
           */
          <iframe
            id="final-preview"
            className="finalpreview"
            title="記事プレビュー"
            sandbox="allow-same-origin"
            srcDoc={preview}
            style={{ height: previewHeight }}
            /*
             * The frame never scrolls: the page does.
             *
             * `overflow: hidden` on the element does not reach the document
             * inside it, so the article kept a second scrollbar of its own
             * beside the window's - two bars for one column of text, and the
             * inner one moved content the page had already accounted for.
             * `scrolling` is the attribute that actually governs a frame's own
             * viewport, and it is the only thing that removes it.
             */
            scrolling="no"
            onLoad={() => {
              fitPreview();
              wireContents();
            }}
          />
        )}
      </Card>

      {exportCard}
    </>
  );
}
